/**
 * ProjectQuoteRequestsSection — section quote requests d'un projet
 * (Chunk 9.L.6 send-only → 9.L.8 lifecycle complet broker UI).
 *
 * Surface broker pour visualiser les QR d'un projet + lifecycle complet :
 *   - création QR (CreateQuoteRequestDialog)
 *   - affichage permanent recipients (au lieu de visible-seulement-dialog)
 *   - ajout/retrait recipients inline (AddRecipientDialog + remove button)
 *   - envoi (SendQuoteRequestDialog 9.L.6, conservé)
 *   - annulation (CancelQuoteRequestDialog) avec reason
 *
 * Hooks consommés :
 *   - useQuoteRequestsByProject (lecture liste 9.F)
 *   - useQuoteRequest (lecture detail nested recipients — chargé par row visible)
 *   - useSendQuoteRequest (9.L.1) — conservé via SendQuoteRequestDialog
 *   - useRemoveQuoteRequestRecipient (9.L.7) — bouton × inline
 *   - useCreateQuoteRequest + useCancelQuoteRequest + useAddQuoteRequestRecipient
 *     consommés indirectement via les 3 nouveaux dialogs (state local).
 *
 * Microcopy outbound : hint « Envois sortants aux assureurs » dans le header
 * pour clarifier mental-model vs ProjectSubmissionsList (réponses inbound).
 *
 * Anti-patterns évités :
 *   - Pas de `<div onClick>` — boutons natifs.
 *   - i18n strict : pas de string FR hardcodée.
 *   - Refetch QR detail post-mutation via queryClient invalidation (hooks 9.L.8
 *     gèrent l'invalidation cache).
 */

import type { QuoteRequestRecipientRow, QuoteRequestRow } from "@kollectivia/shared";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

// 9.T.5.v3.7 — payload minimal pour ouvrir `EditRecipientEmailDialog` sans
// exposer toute la ligne recipient au parent (pas de shape polluant l'API du composant).
type EditingRecipientTarget = {
  quoteRequestId: string;
  recipientId: string;
  insurerCode: string;
  currentEmail: string | null;
};

import { AgentBadge } from "../../../components/AgentBadge";
import { useInsurerCatalog } from "../../../lib/queries/insurers";
import {
  useSendReadiness,
  useQuoteRequest,
  useQuoteRequestsByProject,
  useRemoveQuoteRequestRecipient,
} from "../../../lib/queries/quoteRequests";

import { AddRecipientDialog } from "./AddRecipientDialog";
import { CancelQuoteRequestDialog } from "./CancelQuoteRequestDialog";
import { CreateQuoteRequestDialog } from "./CreateQuoteRequestDialog";
import { EditRecipientEmailDialog } from "./EditRecipientEmailDialog";
import { InsurerEmailBlockNotice } from "./InsurerEmailBlockNotice";
import { QuoteRequestStatusBadge } from "./QuoteRequestStatusBadge";
import { RecipientStatusBadge } from "./RecipientStatusBadge";
import { SendQuoteRequestDialog } from "./SendQuoteRequestDialog";

export type ProjectQuoteRequestsSectionProps = {
  projectId: string;
};

/** Statuts terminaux qui désactivent les boutons Envoyer / Relancer / Annuler / Add/Remove recipients. */
const TERMINAL_STATUSES = new Set(["cancelled", "expired", "fully_received"]);

/** Statuts qui acceptent le cancel (cohérent backend 9.L.7). */
const CANCELLABLE_STATUSES = new Set(["draft", "sent", "partially_received"]);

/** Statuts qui acceptent l'édition de recipients (cohérent backend 9.L.7). */
const RECIPIENT_EDITABLE_STATUSES = new Set(["draft", "sent"]);

export function ProjectQuoteRequestsSection({ projectId }: ProjectQuoteRequestsSectionProps) {
  const { t } = useTranslation();
  const qrs = useQuoteRequestsByProject({ projectId });

  const [createOpen, setCreateOpen] = useState(false);
  const [sendingFor, setSendingFor] = useState<QuoteRequestRow | null>(null);
  const [cancellingFor, setCancellingFor] = useState<QuoteRequestRow | null>(null);
  const [addingRecipientFor, setAddingRecipientFor] = useState<QuoteRequestRow | null>(null);
  const [editingRecipient, setEditingRecipient] = useState<EditingRecipientTarget | null>(null);

  const items = useMemo<QuoteRequestRow[]>(() => qrs.data?.items ?? [], [qrs.data?.items]);

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-medium uppercase tracking-wide text-slate-500">
            {t("quote_requests.section.title")}
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            {t("quote_requests.section.outbound_hint")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="bg-brand-600 hover:bg-brand-700 focus-visible:outline-brand-500 rounded-md px-3 py-1.5 text-xs font-medium text-white shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          {t("quote_requests.section.create_button")}
        </button>
      </div>

      {items.length > 0 && (
        <p className="mb-3 text-xs text-slate-500">
          {t("quote_requests.section.count", { count: items.length })}
        </p>
      )}

      {qrs.isLoading ? (
        <p className="text-sm text-slate-400">{t("app.loading")}</p>
      ) : qrs.isError ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-red-700">{t("quote_requests.section.error")}</p>
          <button
            type="button"
            onClick={() => void qrs.refetch()}
            className="text-brand-700 focus-visible:outline-brand-500 self-start text-xs hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            {t("app.retry")}
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400">{t("quote_requests.section.empty")}</p>
      ) : (
        <ul className="divide-y divide-slate-200">
          {items.map((qr) => (
            <QuoteRequestRowItem
              key={qr.id}
              qr={qr}
              onSend={() => setSendingFor(qr)}
              onCancel={() => setCancellingFor(qr)}
              onAddRecipient={() => setAddingRecipientFor(qr)}
              onEditRecipientEmail={(target) => setEditingRecipient(target)}
            />
          ))}
        </ul>
      )}

      {createOpen && (
        <CreateQuoteRequestDialog projectId={projectId} onClose={() => setCreateOpen(false)} />
      )}
      {sendingFor && (
        <SendQuoteRequestDialog quoteRequest={sendingFor} onClose={() => setSendingFor(null)} />
      )}
      {cancellingFor && (
        <CancelQuoteRequestDialog
          quoteRequestId={cancellingFor.id}
          quoteRequestTitle={cancellingFor.title}
          onClose={() => setCancellingFor(null)}
        />
      )}
      {addingRecipientFor && (
        <AddRecipientDialog
          quoteRequestId={addingRecipientFor.id}
          onClose={() => setAddingRecipientFor(null)}
        />
      )}
      {editingRecipient && (
        <EditRecipientEmailDialog
          quoteRequestId={editingRecipient.quoteRequestId}
          recipientId={editingRecipient.recipientId}
          insurerCode={editingRecipient.insurerCode}
          currentEmail={editingRecipient.currentEmail}
          onClose={() => setEditingRecipient(null)}
        />
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// QuoteRequestRowItem — row enrichi avec recipients permanent + actions
// -----------------------------------------------------------------------------

type RowProps = {
  qr: QuoteRequestRow;
  onSend: () => void;
  onCancel: () => void;
  onAddRecipient: () => void;
  onEditRecipientEmail: (target: EditingRecipientTarget) => void;
};

function QuoteRequestRowItem({
  qr,
  onSend,
  onCancel,
  onAddRecipient,
  onEditRecipientEmail,
}: RowProps) {
  const { t, i18n } = useTranslation();
  const { labelFor } = useInsurerCatalog();
  // Lazy fetch detail (recipients nested) — TanStack Query cache partagé
  // avec CreateQR onSuccess seed → re-render immédiat post-création.
  const detail = useQuoteRequest(qr.id);
  const remove = useRemoveQuoteRequestRecipient();

  const recipients: QuoteRequestRecipientRow[] = useMemo(
    () => detail.data?.recipients ?? [],
    [detail.data?.recipients],
  );

  const isTerminal = TERMINAL_STATUSES.has(qr.status);
  const isFirstSend = qr.status === "draft";
  const readiness = useSendReadiness(isTerminal ? undefined : qr.id);
  const sendReady = !readiness.isError && readiness.data?.can_send === true;
  const canCancel = CANCELLABLE_STATUSES.has(qr.status);
  const canEditRecipients = RECIPIENT_EDITABLE_STATUSES.has(qr.status);

  // 9.L.8 mini-fix: hard-block Send sur le zero-deliverable state.
  // Backend 9.L.1: recipients pending sans `recipient_email` produisent
  // un échec par item; si TOUS échouent → throw 500 EMAIL_SEND_FAILED rollback.
  // Côté UI on désactive Send quand il existe des pending mais aucun avec email,
  // pour éviter que le broker découvre l'impossibilité seulement au clic Send.
  // Partial-send (≥1 pending avec email) reste autorisé : Send actif.
  const hasAnyPending = recipients.some((r) => r.status === "pending");
  const hasPendingWithEmail = recipients.some((r) => r.status === "pending" && r.recipient_email);
  const sendBlockedNoEmail = hasAnyPending && !hasPendingWithEmail;
  // 9.T.5.v3.7 post-smoke — feedback zero-pending : sur une QR déjà envoyée dont
  // tous les recipients sont `sent` (aucun délivrable-en-attente), le bouton
  // « Envoyer aux destinataires en attente » n'a rien à faire (le backend
  // n'enqueue que les `pending`). On le désactive au lieu de laisser un clic sans
  // effet. Le cas `draft` (envoi initial) reste inchangé — sa validation de
  // recipients se fait dans SendQuoteRequestDialog.
  const nothingDeliverablePending = !hasPendingWithEmail;
  const sendBlockedAllSent = !isFirstSend && nothingDeliverablePending && !sendBlockedNoEmail;
  const sendDisabled =
    isTerminal ||
    readiness.isError ||
    readiness.data?.can_send !== true ||
    qr.status === "partially_received" ||
    sendBlockedNoEmail ||
    sendBlockedAllSent;
  const sendHintId = `qr-${qr.id}-send-hint`;

  const onRemoveRecipient = (recipientId: string) => {
    remove.mutate({ quoteRequestId: qr.id, recipientId });
  };

  return (
    <li className="flex flex-col gap-3 py-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-slate-800">
              {qr.title?.trim() || t("quote_requests.section.untitled")}
            </span>
            <QuoteRequestStatusBadge status={qr.status} />
            <AgentBadge displayName={qr.created_by_agent_display_name} variant="created" />
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <span>
              {t("quote_requests.section.created_at_label")}{" "}
              {new Date(qr.created_at).toLocaleDateString(i18n.language)}
            </span>
            {qr.sent_at && (
              <>
                <span aria-hidden="true">·</span>
                <span>
                  {t("quote_requests.section.sent_at_label")}{" "}
                  {new Date(qr.sent_at).toLocaleDateString(i18n.language)}
                </span>
              </>
            )}
            {qr.expected_response_by && (
              <>
                <span aria-hidden="true">·</span>
                {/* 9.AQ.1 — cette date PILOTE les rappels aux assureurs. L'infobulle
                    le dit : sans elle, une demande partirait sans relance, et le
                    conseiller doit savoir ce qu'il regarde. */}
                <span title={t("quote_requests.section.expected_response_by_title")}>
                  {t("quote_requests.section.expected_response_by_label")}{" "}
                  {new Date(`${qr.expected_response_by.slice(0, 10)}T12:00:00`).toLocaleDateString(
                    i18n.language,
                  )}
                </span>
              </>
            )}
          </div>
          {qr.status === "cancelled" && qr.cancelled_reason && (
            <p className="mt-1 text-xs italic text-slate-500">
              <span className="font-medium not-italic">
                {t("quote_requests.section.cancelled_reason_label")}
              </span>{" "}
              {qr.cancelled_reason}
              {qr.cancelled_at && (
                <span className="ml-2 not-italic">
                  · {t("quote_requests.section.cancelled_at_label")}{" "}
                  {new Date(qr.cancelled_at).toLocaleDateString(i18n.language)}
                </span>
              )}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 sm:flex-nowrap">
          <button
            type="button"
            onClick={onSend}
            disabled={sendDisabled}
            aria-describedby={sendBlockedNoEmail || sendBlockedAllSent ? sendHintId : undefined}
            className="bg-brand-600 hover:bg-brand-700 focus-visible:outline-brand-500 rounded-md px-3 py-1.5 text-xs font-medium text-white shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500 disabled:hover:bg-slate-200"
          >
            {isFirstSend
              ? t("quote_requests.section.send_button")
              : t("quote_requests.section.resend_button")}
          </button>
          {canCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 shadow-sm hover:bg-red-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500"
            >
              {t("quote_requests.section.cancel_button")}
            </button>
          )}
        </div>
      </div>

      {!isTerminal && (
        <div className="space-y-1 text-sm">
          {!sendReady && (
            <p role={readiness.isError ? "alert" : "status"} className="text-amber-800">
              {t(
                readiness.isError
                  ? "request_flow.readiness_error"
                  : readiness.isPending
                    ? "app.loading"
                    : "request_flow.preparation_required",
              )}
            </p>
          )}
          <Link
            to={`/quote-requests/${qr.id}/parcours`}
            className="text-brand-700 focus-visible:outline-brand-500 rounded underline focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            {t("request_flow.resume_preparation")}
          </Link>
        </div>
      )}

      {/* Recipients permanent display (9.L.8 — sortis du dialog) */}
      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-xs font-medium text-slate-600">
            {t("quote_requests.section.recipients_label", {
              count: recipients.length,
            })}
          </p>
          {canEditRecipients && (
            <button
              type="button"
              onClick={onAddRecipient}
              className="border-brand-200 text-brand-700 hover:bg-brand-50 focus-visible:outline-brand-500 rounded-md border bg-white px-2.5 py-1 text-xs font-medium shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              {t("quote_requests.section.add_recipient_button")}
            </button>
          )}
        </div>
        {detail.isLoading ? (
          <p className="text-xs text-slate-400">{t("app.loading")}</p>
        ) : recipients.length === 0 ? (
          <p className="text-xs text-slate-400">{t("quote_requests.section.no_recipients")}</p>
        ) : (
          <ul className="space-y-1">
            {recipients.map((r) => {
              const isPending = r.status === "pending";
              const canRemove = canEditRecipients && isPending;
              return (
                <li
                  key={r.id}
                  className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-700"
                >
                  <span className="min-w-0 break-words [overflow-wrap:anywhere]">
                    {labelFor(r.insurer_code)}
                    {r.recipient_email ? (
                      <span className="ml-2 text-slate-500">{r.recipient_email}</span>
                    ) : null}
                  </span>
                  <div className="flex items-center gap-2">
                    {isPending && !r.recipient_email && (
                      <span className="inline-flex items-center rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
                        {t("quote_requests.section.email_missing_badge")}
                      </span>
                    )}
                    {/* 9.T.5.v3.7 — bouton d'édition inline pour saisir /
                        modifier `recipient_email` (statut pending uniquement,
                        parity `canEditRecipients`). Ferme la boucle Send. */}
                    {canEditRecipients && isPending && (
                      <button
                        type="button"
                        onClick={() =>
                          onEditRecipientEmail({
                            quoteRequestId: qr.id,
                            recipientId: r.id,
                            insurerCode: r.insurer_code,
                            currentEmail: r.recipient_email,
                          })
                        }
                        aria-label={t("quote_requests.section.edit_recipient_email_aria", {
                          insurer: labelFor(r.insurer_code),
                        })}
                        className="border-brand-200 text-brand-700 hover:bg-brand-50 focus-visible:outline-brand-500 rounded-md border bg-white px-1.5 py-0.5 text-[10px] font-medium shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2"
                      >
                        {r.recipient_email
                          ? t("quote_requests.section.edit_recipient_email_button")
                          : t("quote_requests.section.add_recipient_email_button")}
                      </button>
                    )}
                    <RecipientStatusBadge
                      status={r.status}
                      deliveryFeedback={r.delivery_feedback}
                    />
                    <InsurerEmailBlockNotice
                      reason={r.email_block_reason}
                      feedback={r.delivery_feedback}
                    />
                    {canRemove && (
                      <button
                        type="button"
                        onClick={() => onRemoveRecipient(r.id)}
                        disabled={remove.isPending}
                        aria-label={t("quote_requests.section.remove_recipient_aria", {
                          insurer: labelFor(r.insurer_code),
                        })}
                        className="rounded-md p-1 text-red-600 hover:bg-red-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500 disabled:opacity-50"
                      >
                        <svg
                          className="h-3.5 w-3.5"
                          viewBox="0 0 16 16"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M3 3l10 10M13 3L3 13"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {sendBlockedNoEmail && (
        <p id={sendHintId} className="text-xs text-amber-800">
          {t("quote_requests.section.send_disabled_no_email_hint")}
        </p>
      )}
      {sendBlockedAllSent && (
        <p id={sendHintId} className="text-xs text-slate-500">
          {t("quote_requests.section.send_disabled_all_sent_hint")}
        </p>
      )}
    </li>
  );
}
